import React, { useMemo } from "react";
import { ArrowsClockwise, Star } from "@phosphor-icons/react";
import useCurrencyRates from "../../hooks/useCurrencyRates";

/**
 * CurrencyTicker (Build 85+, D-026)
 * =================================
 * Scrollable ticker below the main converter card. Feature-flagged
 * behind `TICKER_ENABLED`; the component itself does not check the
 * flag — that gate lives at the CurrencyPanel mount point so an
 * unmounted component adds zero cost when the flag is off.
 *
 * Design contract (from ROADMAP_CURRENCY_TICKER.md):
 *   • Own scroll region; must not fight parent page scroll on iPhone.
 *   • Selected pair (base ↔ target) shown at top with subtle star +
 *     soft orange tint. Never aggressive finance-app colors.
 *   • No fake deltas. If the provider returns only current rates,
 *     display "Live" — never invent movement.
 *   • Failure fallback: keep the last-known rates visible; show a
 *     soft inline "Live rates temporarily unavailable" message inside
 *     the ticker area only. Never crash the page.
 *   • Zero forbidden Apple copy (checked by verify-lockdown.sh).
 */

// Curated seed list — 35 top-travel pairs from the roadmap spec.
// Non-USD-base symbols will be handled naturally by the batch endpoint
// (the base is `You send`; the ticker rows follow whatever the user
// picked as the base currency).
const TICKER_SEED_SYMBOLS = [
  "EUR", "JPY", "GBP", "AUD", "CAD", "MXN", "PHP", "THB", "KRW", "VND",
  "CNY", "INR", "CHF", "SEK", "NOK", "DKK", "SGD", "HKD", "TWD", "IDR",
  "MYR", "AED", "SAR", "TRY", "BRL", "ARS", "COP", "PEN", "CLP", "PLN",
  "CZK", "HUF", "ZAR", "ILS", "EGP",
];

// Friendly country / currency labels (short — the row is narrow on iPhone SE).
const CURRENCY_NAME = {
  EUR: "Euro", JPY: "Japanese Yen", GBP: "British Pound", AUD: "Australian Dollar",
  CAD: "Canadian Dollar", MXN: "Mexican Peso", PHP: "Philippine Peso",
  THB: "Thai Baht", KRW: "Korean Won", VND: "Vietnamese Dong", CNY: "Chinese Yuan",
  INR: "Indian Rupee", CHF: "Swiss Franc", SEK: "Swedish Krona", NOK: "Norwegian Krone",
  DKK: "Danish Krone", SGD: "Singapore Dollar", HKD: "Hong Kong Dollar",
  TWD: "Taiwan Dollar", IDR: "Indonesian Rupiah", MYR: "Malaysian Ringgit",
  AED: "UAE Dirham", SAR: "Saudi Riyal", TRY: "Turkish Lira", BRL: "Brazilian Real",
  ARS: "Argentine Peso", COP: "Colombian Peso", PEN: "Peruvian Sol",
  CLP: "Chilean Peso", PLN: "Polish Zloty", CZK: "Czech Koruna",
  HUF: "Hungarian Forint", ZAR: "South African Rand", ILS: "Israeli Shekel",
  EGP: "Egyptian Pound", USD: "US Dollar",
};

function formatRate(rate) {
  if (rate == null || Number.isNaN(rate)) return "—";
  // Choose precision based on magnitude so small rates keep meaningful
  // digits (e.g. USD/JPY 162.20 vs USD/EUR 0.87207 vs USD/IDR 15,720).
  if (rate >= 1000) return rate.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (rate >= 100) return rate.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (rate >= 1) return rate.toLocaleString(undefined, { maximumFractionDigits: 4 });
  return rate.toLocaleString(undefined, { maximumFractionDigits: 5 });
}

export default function CurrencyTicker({
  base = "USD",
  selectedTarget = null,
  onSelectPair = null,
}) {
  // Move the selected pair to the top of the list (once) so it renders
  // first without breaking cache key ordering (the hook normalizes).
  const orderedSymbols = useMemo(() => {
    const base_up = (base || "USD").toUpperCase();
    const sel = (selectedTarget || "").toUpperCase();
    const rest = TICKER_SEED_SYMBOLS.filter(s => s !== base_up && s !== sel);
    return sel && sel !== base_up ? [sel, ...rest] : rest;
  }, [base, selectedTarget]);

  const { rates, status, error, source, refresh } = useCurrencyRates(
    base,
    orderedSymbols,
    { enabled: true },
  );

  const isError = status === "error" && Object.keys(rates).length === 0;
  const isPartial = status === "error" && Object.keys(rates).length > 0;

  return (
    <div
      className="rounded-2xl bg-white border border-navy/10 overflow-hidden"
      data-testid="currency-ticker"
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="text-navy-soft text-[11px] uppercase tracking-[0.18em] font-jakarta font-bold">
          Live rates
        </div>
        <button
          type="button"
          onClick={refresh}
          className="text-navy-soft hover:text-navy transition p-1 rounded-full"
          aria-label="Refresh live rates"
          data-testid="currency-ticker-refresh"
        >
          <ArrowsClockwise
            size={14}
            className={status === "loading" ? "animate-spin" : ""}
          />
        </button>
      </div>

      {/* Own scroll region — max height keeps the parent page scroll usable */}
      <div
        className="max-h-[280px] overflow-y-auto overscroll-contain divide-y divide-navy/5"
        data-testid="currency-ticker-scroll"
      >
        {isError && (
          <div
            className="p-4 text-center text-navy-soft/80 text-sm font-jakarta italic"
            data-testid="currency-ticker-error"
          >
            Live rates temporarily unavailable
          </div>
        )}

        {!isError &&
          orderedSymbols.map((sym) => {
            const rate = rates[sym];
            const isSelected = selectedTarget && sym === selectedTarget.toUpperCase();
            const clickable = typeof onSelectPair === "function";
            const commonClasses =
              "w-full flex items-center justify-between px-4 py-2.5 transition text-left " +
              (isSelected ? "bg-orange/5" : "hover:bg-navy/[0.02] active:bg-navy/[0.04]");
            const inner = (
              <>
                <div className="flex items-center gap-2 min-w-0">
                  {isSelected && (
                    <Star
                      size={12}
                      weight="fill"
                      className="text-orange shrink-0"
                      aria-hidden="true"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="font-jakarta font-bold text-sm text-navy tabular-nums">
                      {base.toUpperCase()}/{sym}
                    </div>
                    <div className="font-jakarta text-[11px] text-navy-soft truncate">
                      {CURRENCY_NAME[sym] || sym}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0 pl-2">
                  <div className="font-jakarta font-bold text-sm text-navy tabular-nums">
                    {formatRate(rate)}
                  </div>
                  <div className="font-jakarta text-[10px] text-emerald-600 uppercase tracking-wider font-semibold">
                    Live
                  </div>
                </div>
              </>
            );
            return clickable ? (
              <button
                key={sym}
                type="button"
                onClick={() => onSelectPair(sym)}
                className={commonClasses}
                data-testid={`currency-ticker-row-${sym}`}
                data-selected={isSelected ? "1" : "0"}
                aria-label={`Set destination to ${CURRENCY_NAME[sym] || sym}`}
                aria-pressed={isSelected ? "true" : "false"}
              >
                {inner}
              </button>
            ) : (
              <div
                key={sym}
                className={commonClasses}
                data-testid={`currency-ticker-row-${sym}`}
                data-selected={isSelected ? "1" : "0"}
              >
                {inner}
              </div>
            );
          })}

        {isPartial && (
          <div
            className="px-4 py-2 text-center text-navy-soft/70 text-[11px] font-jakarta italic"
            data-testid="currency-ticker-partial"
          >
            Some rates couldn't refresh — showing last-known values
          </div>
        )}
      </div>

      {source && (
        <div className="px-4 py-2 flex items-center justify-end gap-2">
          {source === "preview" && (
            <span
              data-testid="currency-ticker-preview-badge"
              className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 font-jakarta font-bold text-[9px] uppercase tracking-wider px-2 py-0.5"
            >
              Preview rates
            </span>
          )}
          <span className="text-navy-soft/70 text-[10px] font-jakarta uppercase tracking-wider">
            Source: {source}
          </span>
        </div>
      )}
    </div>
  );
}
